import {Injectable} from '@angular/core';
import {MinizaemApiClientService} from "../../core/services/minizaem-api-client.service";
import {map, Observable} from "rxjs";
import {LoanType} from "../models/loan-type";
import {Parameters} from "../models/loan-application-step/parameters";
import {Loan} from "../models/loan";
import {LoanInstalment} from "../models/loan-instalment";
import {LoanInstalmentComponentTypes} from "../models/loan-instalment-component-types";
import {LoanConfirmationData} from "../models/loan-confirmation-data";
import {LoanActionPermissions} from "../models/loan-action-permission";

@Injectable({
  providedIn: 'root'
})
export class LoanService extends MinizaemApiClientService {

  getAllLoanTypeFromApi(): Observable<LoanType[]> {
    return this.http.get<LoanType[]>(this.buildURI(`api/loan-type/show`));
  }

  initForParameters(loanParameters: Parameters): Observable<{
    "principalDue": number,
    "interestDue": number,
    "failureFeeDue": number,
    "totalAmountDue": number,
    "annualPercentageRate": number,
  }> {
    return this.http.get(this.buildURI(`api/loan/init-for-parameters?loanType=${loanParameters.loanType}&principal=${loanParameters.principal}&period=${loanParameters.period}`)).pipe(
      map((response: any) => {
        return {
          "principalDue": response.aggregatedAmounts.principalDue,
          "interestDue": response.aggregatedAmounts.interestDue,
          "failureFeeDue": response.aggregatedAmounts.failureFeeDue,
          "totalAmountDue": response.aggregatedAmounts.totalAmountDue,
          "annualPercentageRate": response.aggregatedAmounts.annualPercentageRate,
        };
      })
    );
  }

  showCurrentForCustomer(): Observable<Loan> {
    return this.http.get<Loan>(this.buildURI(`api/loan/show-current-for-customer`));
  }
  showActionPermissions(): Observable<LoanActionPermissions> {
    return this.http.get<LoanActionPermissions>(this.buildURI(`api/loan/action-permissions`));
  }

  showConfirmationData(encryptedId: string): Observable<LoanConfirmationData> {
    return this.http.get<LoanConfirmationData>(this.buildURI(`api/loan/show-confirmation-data/${encryptedId}`));
  }

  create(loan: Loan): Observable<Loan> {
    return this.http.post<Loan>(this.buildURI(`api/loan/create`), loan);
  }

  confirm(data: any): Observable<any> {
    return this.http.put(this.buildURI(`api/loan/confirm`), data);
  }

  generatePreContractualInformationForParameters(loanParameters: Parameters): Observable<any> {
    return this.http.get(this.buildURI(
      `api/loan/generate-pre-contractual-information-for-parameters?loanType=${loanParameters.loanType}&principal=${loanParameters.principal}&period=${loanParameters.period}`
    ));
  }

  calculateAggregateAmount(loan: Loan, loanInstalmentComponentType: LoanInstalmentComponentTypes = LoanInstalmentComponentTypes.TotalAmountDue): number {
    let aggregateAmount = 0.0;

    const loanInstalmentPropertyName = loanInstalmentComponentType.toLowerCase()
      .split('_')
      .map((word, index) =>
        index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
      )
      .join('') as keyof LoanInstalment;

    // Check if the property exists on a sample LoanInstalment
    if (
      loan.loanInstalments.length > 0 &&
      !(loanInstalmentPropertyName in loan.loanInstalments[0])
    ) {
      throw new Error(`Loan instalments have no property ${loanInstalmentPropertyName}!`);
    }

    for (const loanInstalment of loan.loanInstalments) {
      const value = loanInstalment[loanInstalmentPropertyName];
      if (typeof value === "number") {
        aggregateAmount += value;
      }
    }

    return aggregateAmount;
  }
}
